import type { JSX } from "react";
import type { Turn } from "./types";

function ErrorInformation({ turn }: { turn: Turn }): JSX.Element {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-center mb-3">
        <span className="text-red-600 text-xl mr-2">⚠️</span>
        <h3 className="text-lg font-medium text-red-800">Error Information</h3>
      </div>

      {turn.error_text && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-red-700 mb-2">Error Message</label>
          <div className="bg-white p-3 rounded-sm border border-red-200">
            <p className="text-red-800 font-mono text-sm">{turn.error_text}</p>
          </div>
        </div>
      )}

      {turn.error_stack && (
        <div>
          <label className="block text-sm font-medium text-red-700 mb-2">Stack Trace</label>
          <div className="bg-white p-3 rounded-sm border border-red-200 max-h-96 overflow-y-auto">
            <pre className="text-red-800 font-mono text-xs whitespace-pre-wrap">
              {turn.error_stack}
            </pre>
          </div>
        </div>
      )}

      {!turn.error_text && !turn.error_stack && (
        <p className="text-red-700">Error flag is set but no error details are available.</p>
      )}
    </div>
  );
}

function TroubleshootingTips(): JSX.Element {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-center mb-2">
        <span className="text-amber-600 text-lg mr-2">💡</span>
        <h4 className="text-amber-800 font-medium">Troubleshooting Tips</h4>
      </div>
      <ul className="text-amber-700 text-sm space-y-1 list-disc list-inside">
        <li>Check the stack trace for the specific line and function where the error occurred</li>
        <li>Review the merged context in the Context tab for any unusual input data</li>
        <li>Look at the actions that were executed before this error occurred</li>
        <li>Check if this is a recurring error pattern across multiple turns</li>
      </ul>
    </div>
  );
}

export default function TurnErrorTab({ turn }: { turn: Turn }): JSX.Element {
  return (
    <div className="space-y-6">
      <ErrorInformation turn={turn} />
      <TroubleshootingTips />
    </div>
  );
}
